import { useTranslation } from "react-i18next";

export type Direction = "ltr" | "rtl";

/**
 * The one place the app decides which way it reads.
 *
 * There used to be three. App.tsx set html[dir] from its own `lang` state,
 * which fell back to navigator.language; SearchForm set dir on the booking
 * flow from i18n.language, which falls back to localStorage; and FormHeader
 * picked its translations straight off window.location.pathname. Whenever
 * those fallbacks disagreed — an entry URL with no /en or /ar prefix, or a
 * stored language different from the browser's — the document said one thing
 * and the section inside it said another. That is what produced Arabic copy
 * inside a left-to-right vehicle page, and an English page whose layout and
 * CTA were mirrored.
 *
 * i18n.language is the value the components already render from, so it is the
 * one that wins. Everything else follows it.
 *
 * `startsWith` rather than `=== "ar"`: a stored or negotiated tag can be
 * regional ("ar-SA"), and an equality test silently reads that as English.
 */
export function directionFor(language?: string): Direction {
  return language?.toLowerCase().startsWith("ar") ? "rtl" : "ltr";
}

export default function useDirection(): Direction {
  const { i18n } = useTranslation();
  return directionFor(i18n.language);
}
